/**
 * The shape of a blow.
 *
 * Pure arithmetic, in core, for one reason: the same swing has to be drawn on
 * the arm you can see over your own shoulder, on the fist in front of the
 * camera, and eventually on somebody else's body across the network. Three
 * places drawing "a blow" from three different curves is three blows.
 *
 * It is not a sine. A strike is fast down and slow back: most of the time is
 * the recovery, and a symmetric curve reads as a wave rather than as a hit.
 */

/** How long one blow lasts, in seconds. */
export const STRIKE_SECONDS = 0.28;

/** The fraction of it spent going down. The rest is coming back. */
const DOWN = 0.3;

/** How far the arm comes forward at the bottom of a blow, in radians. */
export const STRIKE_REACH = 2.1;

/**
 * How far through a blow, 0..1, given how long is left of it.
 *
 * Takes the remaining time rather than the elapsed, because that is what a
 * countdown timer holds and converting at every call site is how one of the
 * three ends up off by a frame.
 */
export function strikeProgress(secondsLeft: number): number {
  if (secondsLeft <= 0) return 0;
  return Math.min(1, Math.max(0, 1 - secondsLeft / STRIKE_SECONDS));
}

/**
 * The angle of the arm at this point in a blow, in radians.
 *
 * Nought at rest, `STRIKE_REACH` at the bottom. Eased on the way back so the
 * arm settles rather than snapping to attention.
 */
export function strikeAngle(progress: number): number {
  if (progress <= 0 || progress >= 1) return 0;
  if (progress < DOWN) return STRIKE_REACH * (progress / DOWN);
  const back = (progress - DOWN) / (1 - DOWN);
  // Cosine ease: quick at first, slow as it arrives.
  return STRIKE_REACH * (0.5 + 0.5 * Math.cos(Math.PI * back));
}
